import { useState } from "react";
import { BACKEND_URL } from "../constants";
import { Asset, AssetRequestMetadata, ListAssetsFilter, UploadAssetResult } from "../types/types";
import { useGetAsyncAny, useListAny } from ".";


export const useUploadAsset = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadAsset = async (file: File, metadata: AssetRequestMetadata): Promise<UploadAssetResult> => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("jwt");
      if (!token) {
        throw new Error("No token found");
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("metadata", JSON.stringify(metadata));

      const response = await fetch(`${BACKEND_URL}/assets`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: token,
        },
      });

      if (!response.ok) {
        const text = await response.text();
        setError(text);
        return { success: false, message: text };
      }

      const data = await response.json() as { assetId: string };
      return { success: true, data };
    } catch (err: any) {
      console.error("Upload failed:", err);
      setError(err.message);
      return { success: false, message: err.message };
    } finally {
      setLoading(false);
    }
  };

  return { uploadAsset, loading, error };
};

export const useGetGroupAssets = (filter: ListAssetsFilter) => {
  const assetTypes = filter.assetTypes.join(",");
  const params = new URLSearchParams({ assetTypes });
  return useListAny<Asset[]>(`/assets?${params}`);
}

export const useGetAsset = () => {
  return useGetAsyncAny<Asset>();
}

export const useGetAssetData = () => {
  const fetchAny = async (path: string): Promise<any> => {
    return new Promise((resolve, reject) => {
      const token = localStorage.getItem("jwt");
      fetch(`${BACKEND_URL}${path}/data`, {
        method: "GET",
        headers: {
          Authorization: token ?? "",
        },
        cache: "no-store"
      })
        .then(response => {
          if (!response.ok) {
            reject(new Error(`HTTP error! Status: ${response.status}`));
          }
          resolve(response.arrayBuffer());
        })
        .catch(err => reject(err))
    })
  }
  return { fetch: fetchAny };
}

interface UseDeleteAssetResponse<T> {
  isLoading: boolean;
  error: Error | null;
  deleteFn: (assetId: string) => Promise<T | null>;
}

export function useDeleteAsset<T = any>(): UseDeleteAssetResponse<T> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteFn = async (assetId: string): Promise<T | null> => {
    if (!assetId) {
      setError(new Error("assetId is required"));
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("jwt");

      const response = await fetch(`${BACKEND_URL}/assets/${assetId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "Authorization": token ?? "",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = (await response.json()) as T;
      return data;
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err);
      } else {
        setError(new Error("An unknown error occurred"));
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, error, deleteFn };
}